import { Headphones, Coffee, LogOut, Phone, PhoneIncoming } from 'lucide-react';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { 
  getAgentStateColor,
  getAgentStateBadgeColor, 
  getAgentStateLabel,
  getEffectiveAgentState,
} from '@/lib/queue-helpers';

interface AgentStatusBadgeProps {
  state?: string;
  status?: string;
  showIcon?: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export function AgentStatusBadge({ 
  state, 
  status, 
  showIcon = true, 
  size = 'md',
  className = '',
}: AgentStatusBadgeProps) {
  const effectiveState = getEffectiveAgentState({ state, status });
  const badgeColor = getAgentStateBadgeColor(effectiveState);
  const label = getAgentStateLabel(effectiveState);
  
  const iconSize = size === 'sm' ? 'h-3 w-3' : size === 'lg' ? 'h-5 w-5' : 'h-4 w-4';
  const textSize = size === 'sm' ? 'text-xs' : size === 'lg' ? 'text-sm' : 'text-xs';
  
  const getIcon = () => {
    switch (effectiveState) {
      case 'In a queue call':
        return <Phone className={iconSize} />;
      case 'Receiving':
        return <PhoneIncoming className={`${iconSize} animate-pulse`} />;
      case 'Available':
        return <Headphones className={iconSize} />;
      case 'On Break':
        return <Coffee className={iconSize} />;
      case 'Logged Out':
        return <LogOut className={iconSize} />;
      default:
        return <Headphones className={iconSize} />;
    }
  };
  
  return (
    <Badge 
      variant="secondary" 
      className={`${badgeColor} ${textSize} ${className}`}
    >
      {showIcon && <span className="mr-1">{getIcon()}</span>}
      {label}
    </Badge>
  );
}

interface AgentStatusDotProps {
  state?: string;
  status?: string;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export function AgentStatusDot({ 
  state, 
  status, 
  size = 'md',
  className = '' 
}: AgentStatusDotProps) {
  const effectiveState = getEffectiveAgentState({ state, status });
  const dotColor = getAgentStateColor(effectiveState);
  
  const dotSize = size === 'sm' ? 'h-2 w-2' : size === 'lg' ? 'h-4 w-4' : 'h-3 w-3';
  
  return (
    <div className={`${dotSize} rounded-full ${dotColor} ${className}`} />
  );
}

interface AgentStatusIconProps {
  status?: string;
  className?: string;
}

export function AgentStatusIcon({ status, className = '' }: AgentStatusIconProps) {
  switch (status) {
    case 'Available':
    case 'Available (On Demand)':
      return <Headphones className={`h-3 w-3 text-green-500 ${className}`} />;
    case 'On Break':
      return <Coffee className={`h-3 w-3 text-yellow-500 ${className}`} />;
    case 'Logged Out':
      return <LogOut className={`h-3 w-3 text-gray-500 ${className}`} />;
    default:
      return <Headphones className={`h-3 w-3 text-gray-400 ${className}`} />;
  }
}
